import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import DischargeSummaryService from "../../../../services/DischargeSummaryService";
// Renders the draft as real A4 pages (also pulls the `.clinical-document-preview` styling).
import PaginatedClinicalDocument from "../../../../components/ClinicalEditor/PaginatedDocument";

/**
 * ViewDischargeDraftModal
 *
 * Read-only viewer for the saved/edited discharge-summary draft. Fetches the
 * draft HTML for an admission from `POST /get_edited_discharge_summary/`
 * (returns the raw HTML string) and renders it in an A4 document container.
 *
 * Intentionally minimal: this is a standalone "view" action, not part of the
 * generate/edit wizard — no finalize / save / PDF actions here.
 */
const ViewDischargeDraftModal = ({ open, onClose, admissionId, authHeaders }) => {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset state when the modal closes so a stale draft never flashes on reopen.
      setLoading(false);
      setHtml("");
      setErrorMessage("");
      return;
    }
    if (!admissionId) {
      setErrorMessage("No admission selected.");
      return;
    }

    let cancelled = false;

    const fetchDraft = async () => {
      setLoading(true);
      setErrorMessage("");
      setHtml("");
      try {
        const data = await DischargeSummaryService.getEditedDischargeSummary(
          admissionId,
          authHeaders
        );

        // Happy path is a raw HTML string; tolerate JSON-wrapped + nested variants too.
        let draftHtml =
          typeof data === "string"
            ? data
            : data?.html_content ||
              data?.html ||
              data?.edited_html ||
              data?.data?.html_content ||
              data?.data?.html ||
              "";
        draftHtml = (draftHtml || "").trim();

        // Fallback: the edited HTML now also rides INSIDE final_json on save (the blob
        // save_discharge_summary reliably persists). If get_edited has nothing, read it
        // from the saved record so the viewer isn't blank when the separate edited-HTML
        // store isn't wired on the backend.
        if (!draftHtml) {
          try {
            const rec = await DischargeSummaryService.getDischargeSummary(
              admissionId,
              authHeaders
            );
            const body =
              rec && typeof rec.data === "object" && rec.data !== null
                ? { ...rec, ...rec.data }
                : rec || {};
            let finalJson = body.final_json || body.finalJson || null;
            if (typeof finalJson === "string") {
              try {
                finalJson = JSON.parse(finalJson);
              } catch {
                finalJson = null;
              }
            }
            const embeddedHtml =
              finalJson && typeof finalJson === "object"
                ? finalJson.html_content || finalJson.htmlContent || ""
                : "";
            draftHtml = (
              body.html_content ||
              body.htmlContent ||
              embeddedHtml ||
              ""
            ).trim();
          } catch (fallbackErr) {
            console.warn(
              "[DischargeDraft] fallback get_discharge_summary failed:",
              fallbackErr?.message
            );
          }
        }

        if (!cancelled) setHtml(draftHtml);
      } catch (err) {
        console.error("Failed to fetch saved discharge draft:", err?.message);
        if (!cancelled) {
          setErrorMessage(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load the saved draft."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDraft();

    return () => {
      cancelled = true;
    };
    // authHeaders is read from closure on purpose — it's recreated by the parent
    // on every render, so depending on it here would refetch on each re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admissionId]);

  // A blank A4 "page" surface used ONLY for the status states (loading / error /
  // empty). The actual draft already renders as its own A4 page via the
  // `.clinical-document-preview` class (white bg, A4 size, shadow, padding), so it
  // must NOT be wrapped in another white page — a page nested inside a page is what
  // made the draft look like it was floating on an extra white screen.
  const a4Surface = {
    width: "210mm",
    minHeight: "297mm",
    bgcolor: "#fff",
    boxShadow: 3,
    boxSizing: "border-box",
    p: 5,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={a4Surface}>
          <CircularProgress />
        </Box>
      );
    }

    if (errorMessage) {
      return (
        <Box sx={a4Surface}>
          <Alert severity="error" sx={{ width: "100%" }}>
            {errorMessage}
          </Alert>
        </Box>
      );
    }

    if (!html) {
      return (
        <Box sx={a4Surface}>
          <Typography variant="body1" color="text.secondary">
            No saved draft found for this patient.
          </Typography>
        </Box>
      );
    }

    return <PaginatedClinicalDocument html={html} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Saved Discharge Draft</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ bgcolor: "#e0e0e0", p: 4, display: "flex", justifyContent: "center" }}
      >
        {renderBody()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDischargeDraftModal;
